import React, { useState } from 'react';

const MysteryMathDetectiveGame = ({ gameData, onComplete }) => {
    const [currentClueIndex, setCurrentClueIndex] = useState(0);
    const [score, setScore] = useState(0);
    const [userAnswer, setUserAnswer] = useState('');
    const [showHint, setShowHint] = useState(false);
    const [feedback, setFeedback] = useState(null);
    const [solved, setSolved] = useState(false);

    const currentClue = gameData?.clues?.[currentClueIndex];

    const handleKeyPress = (val) => {
        if (feedback) return;
        if (navigator.vibrate) navigator.vibrate(20);
        if (val === 'C') {
            setUserAnswer('');
        } else {
            if (userAnswer.length >= 10) return;
            setUserAnswer(prev => prev + val);
        }
    };

    const checkAnswer = () => {
        if (!userAnswer || feedback) return;
        const normalizedAnswer = userAnswer.trim().toLowerCase();
        const correctAnswer = currentClue.answer.toLowerCase();

        if (normalizedAnswer === correctAnswer) {
            const pointsToAdd = gameData.points_per_clue || 20;
            const newScore = score + pointsToAdd;
            setScore(newScore);
            setFeedback({ type: 'success', message: 'UŽUOMINA SURINKTA! 🔍' });
            if (navigator.vibrate) navigator.vibrate([100, 50, 100]);

            setTimeout(() => {
                if (currentClueIndex < gameData.clues.length - 1) {
                    setCurrentClueIndex(prev => prev + 1);
                    setUserAnswer('');
                    setShowHint(false);
                    setFeedback(null);
                } else {
                    setSolved(true);
                    if (navigator.vibrate) navigator.vibrate([200, 100, 200, 100, 300]);
                }
            }, 3000);
        } else {
            setFeedback({ type: 'error', message: 'KAŽKAS NESUTAMPA... 🕵️‍♂️' });
            if (navigator.vibrate) navigator.vibrate([50, 50, 50]);
            setTimeout(() => setFeedback(null), 2000);
        }
    };

    if (!gameData || !gameData.clues || !currentClue) {
        return (
            <div className="premium-glass rounded-xl md:rounded-[3rem] p-4 md:p-12 text-center border-orange-100 animate-pulse bg-white">
                <div className="text-lg md:text-4xl md:text-[100px] mb-8">🔍</div>
                <h3 className="text-base md:text-3xl font-black text-orange-900 tracking-tighter uppercase">Analizuojama byla...</h3>
            </div>
        );
    }

    const progress = (currentClueIndex / gameData.clues.length) * 100;

    return (
        <div className="space-y-3 md:space-y-8 animate-slide-up select-none">
            {/* Detective HUD Header */}
            <div className="premium-glass rounded-lg md:rounded-[2.5rem] p-3 md:p-8 border-white/40 shadow-2xl relative overflow-hidden bg-amber-50">
                <div className="absolute top-0 right-0 w-64 h-64 bg-orange-500/5 rounded-full blur-[80px] pointer-events-none"></div>
                <div className="flex flex-col md:flex-row items-center justify-between gap-2 md:p-6 relative z-10">
                    <div className="flex items-center gap-2 md:p-6">
                        <div className="w-20 h-20 bg-orange-100 rounded-3xl flex items-center justify-center text-lg md:text-4xl shadow-inner border-2 border-orange-200 animate-float">
                            🕵️‍♂️
                        </div>
                        <div>
                            <h3 className="text-lg md:text-4xl font-black text-slate-800 tracking-tighter leading-tight uppercase">
                                Matematikos <span className="text-orange-600">Detektyvas</span>
                            </h3>
                            <p className="text-slate-500 font-bold italic font-serif">
                                Išspręsk mįsles ir atskleisk paslaptį!
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center gap-2 md:p-6">
                        <div className="bg-white/50 px-8 py-4 rounded-[2rem] border-2 border-white shadow-inner">
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-2">Įrodymai</p>
                            <p className="text-base md:text-3xl font-black text-orange-600 leading-none">⭐ {score}</p>
                        </div>
                        <div className="bg-white/50 px-8 py-4 rounded-[2rem] border-2 border-white shadow-inner text-center min-w-[120px]">
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-2">Užuomina</p>
                            <p className="text-base md:text-3xl font-black text-slate-800 leading-none">{currentClueIndex + 1}/{gameData.clues.length}</p>
                        </div>
                    </div>
                </div>

                <div className="mt-8 w-full bg-slate-200/50 rounded-full h-4 p-1 shadow-inner overflow-hidden border-2 border-slate-300">
                    <div 
                        className="h-full bg-gradient-to-r from-yellow-400 via-orange-500 to-rose-600 rounded-full transition-all duration-1000 shadow-[0_0_20px_rgba(245,158,11,0.4)]"
                        style={{ width: `${progress}%` }}
                    />
                </div>
            </div>

            {/* Mystery Briefing Board */}
            <div className="premium-glass rounded-2xl md:rounded-[4rem] p-4 md:p-10 lg:p-4 md:p-14 shadow-2xl border-white/60 bg-white/60 backdrop-blur-xl relative overflow-hidden flex flex-col items-center">
                <div className="absolute top-0 right-0 p-4 md:p-12 text-2xl md:text-6xl md:text-[200px] opacity-[0.03] select-none pointer-events-none rotate-12">🔍</div>
                
                <div className="w-full max-w-5xl space-y-4 md:space-y-12 relative z-10">
                    <div className="text-center space-y-4">
                        <h2 className="text-xl md:text-5xl lg:text-2xl md:text-6xl font-black text-slate-900 tracking-tighter uppercase leading-none border-b-8 border-orange-100 inline-block px-10 pb-4">
                            {gameData.mystery.title}
                        </h2>
                        <div className="pt-6">
                            <p className="text-2xl lg:text-base md:text-3xl font-bold text-slate-600 italic font-serif leading-relaxed px-10">
                                "{gameData.mystery.intro}"
                            </p>
                        </div>
                    </div>

                    <div className="h-1 w-full bg-gradient-to-r from-transparent via-slate-200 to-transparent" />

                    {!solved ? (
                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:p-12 items-center">
                            {/* Evidence Card (Left) */}
                            <div className="lg:col-span-6 space-y-4 md:space-y-10">
                                <div className="text-2xl md:text-6xl md:text-[180px] drop-shadow-[0_30px_60px_rgba(245,158,11,0.4)] animate-float text-center">
                                    {currentClue.emoji}
                                </div>
                                <div className="bg-white rounded-[3.5rem] p-4 md:p-10 shadow-2xl border-4 border-orange-50 relative group overflow-hidden">
                                    <div className="absolute top-0 right-0 p-2 md:p-6 text-2xl md:text-6xl opacity-[0.05] select-none pointer-events-none group-hover:scale-125 transition-transform">📄</div>
                                    <h4 className="text-[10px] font-black text-orange-400 uppercase tracking-[0.5em] mb-6">Užduoties Protokolas</h4>
                                    <p className="text-2xl lg:text-base md:text-3xl font-bold text-slate-700 leading-relaxed italic font-serif mb-8">
                                        "{currentClue.clue}"
                                    </p>
                                    <div className="bg-orange-50 p-3 md:p-8 rounded-lg md:rounded-[2.5rem] border-2 border-orange-100 text-center">
                                        <h3 className="text-lg md:text-4xl lg:text-xl md:text-5xl font-black text-slate-900 tracking-tighter uppercase">
                                            {currentClue.question}
                                        </h3>
                                    </div>
                                </div>
                            </div>

                            {/* Forensic Analysis HUD (Right) */}
                            <div className="lg:col-span-6 space-y-3 md:space-y-8">
                                <div className="bg-slate-900 rounded-2xl md:rounded-[4rem] p-4 md:p-10 shadow-[0_40px_80px_rgba(0,0,0,0.4)] border-8 border-slate-800 relative overflow-hidden">
                                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(245,158,11,0.1)_0%,transparent_70%)] pointer-events-none" />
                                    
                                    <div className="relative z-10 space-y-4 md:space-y-10">
                                        <div className="text-center space-y-4">
                                            <p className="text-[10px] font-black text-orange-400 uppercase tracking-[0.4em]">Rezultatų Įvedimas</p>
                                            <div className="h-32 flex items-center justify-center">
                                                <p className={`text-8xl font-black font-mono tracking-widest ${feedback?.type === 'error' ? 'text-rose-500 animate-shake' : 'text-white drop-shadow-[0_0_20px_rgba(255,255,255,0.4)]'}`}>
                                                    {userAnswer || '____'}
                                                </p>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-3 gap-4">
                                            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 'C', 0, '✓'].map((btn) => (
                                                <button
                                                    key={btn}
                                                    onClick={() => btn === '✓' ? checkAnswer() : handleKeyPress(btn)}
                                                    className={`
                                                        h-24 rounded-[2rem] text-lg md:text-4xl font-black flex items-center justify-center transition-all duration-150 border-b-[10px] shadow-lg
                                                        ${btn === '✓' ? 'bg-emerald-500 text-white border-emerald-700 active:bg-emerald-600 active:translate-y-2 active:border-b-0' : 
                                                          btn === 'C' ? 'bg-rose-500 text-white border-rose-700 active:bg-rose-600 active:translate-y-2 active:border-b-0' :
                                                          'bg-white text-slate-800 border-slate-200 hover:bg-slate-50 active:translate-y-2 active:border-b-0'}
                                                    `}
                                                >
                                                    {btn}
                                                </button>
                                            ))}
                                        </div>

                                        <div className="pt-6">
                                            <button
                                                onClick={() => { if (navigator.vibrate) navigator.vibrate(20); setShowHint(!showHint); }}
                                                className={`w-full py-4 rounded-[2rem] font-black uppercase tracking-widest transition-all ${showHint ? 'bg-indigo-600 text-white shadow-xl' : 'bg-slate-800 text-indigo-400 hover:bg-slate-700'}`}
                                            >
                                                {showHint ? '💡 SLĖPTI PATARIMĄ' : '🔦 REIKIA PAGALBOS?'}
                                            </button>
                                            {showHint && (
                                                <div className="mt-6 bg-indigo-900/50 p-3 md:p-8 rounded-lg md:rounded-[2.5rem] border-2 border-indigo-500/30 text-indigo-100 font-bold italic font-serif leading-relaxed animate-pop-in">
                                                    "Detektyvo patarimas: {currentClue.hint}"
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="animate-pop-in space-y-4 md:space-y-12 text-center py-4 md:py-10">
                            <div className="text-2xl md:text-6xl md:text-[200px] drop-shadow-[0_40px_80px_rgba(16,185,129,0.3)] animate-bounce">🥇🕵️‍♂️</div>
                            <div className="space-y-6">
                                <h2 className="text-7xl font-black text-emerald-600 tracking-tighter uppercase leading-none">Paslaptis Išnarpliota!</h2>
                                <p className="text-base md:text-3xl font-bold text-slate-400 italic font-serif">"Byla sėkmingai uždaryta, detektyve!"</p>
                            </div>
                            
                            <div className="bg-slate-900 rounded-2xl md:rounded-[4rem] p-4 md:p-16 text-white shadow-[0_40px_100px_rgba(0,0,0,0.5)] border-8 border-slate-800 max-w-3xl mx-auto relative overflow-hidden group">
                                <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(16,185,129,0.1)_0%,transparent_80%)] pointer-events-none" />
                                <div className="absolute top-0 right-0 p-4 md:p-10 text-9xl opacity-[0.03] select-none pointer-events-none group-hover:scale-125 transition-transform">📄</div>
                                <p className="text-base md:text-3xl lg:text-lg md:text-4xl font-bold text-white leading-relaxed font-serif italic px-6 relative z-10">
                                    "{gameData.solution}"
                                </p>
                            </div>

                            <button
                                onClick={() => onComplete?.(score)}
                                className="premium-button-3d px-24 py-12 bg-gradient-to-r from-emerald-500 via-teal-500 to-green-600 text-white rounded-[3.5rem] font-black text-base md:text-3xl shadow-2xl shadow-emerald-900/40 uppercase tracking-[0.2em] border-b-[12px] border-emerald-800"
                            >
                                UŽDARYTI BYLĄ ✓
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Forensic Feedback Overlay */}
            {feedback && !solved && (
                <div className="fixed inset-0 flex items-center justify-center z-[1000] p-2 md:p-6">
                    <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-xl animate-fade-in" />
                    <div className={`
                        max-w-2xl w-full p-4 md:p-16 rounded-2xl md:rounded-[4rem] bg-white shadow-[0_40px_100px_rgba(0,0,0,0.5)] relative animate-pop-in border-[12px]
                        ${feedback.type === 'success' ? 'border-emerald-500' : 'border-rose-500'}
                    `}>
                        <div className="absolute -top-4 md:p-14 left-1/2 -translate-x-1/2 w-28 h-28 bg-white rounded-full flex items-center justify-center text-2xl md:text-6xl shadow-2xl border-8 border-inherit animate-bounce">
                            {feedback.type === 'success' ? '🕵️‍♂️' : '🔎'}
                        </div>
                        
                        <div className="text-center space-y-3 md:space-y-8">
                            <h2 className={`text-xl md:text-5xl font-black uppercase tracking-tighter ${feedback.type === 'success' ? 'text-emerald-600' : 'text-rose-600'}`}>
                                {feedback.message}
                            </h2>

                            <div className="flex items-center justify-center gap-4">
                                <div className="flex gap-2">
                                    <div className="w-4 h-4 bg-slate-200 rounded-full animate-bounce" />
                                    <div className="w-4 h-4 bg-slate-200 rounded-full animate-bounce [animation-delay:0.2s]" />
                                    <div className="w-4 h-4 bg-slate-200 rounded-full animate-bounce [animation-delay:0.4s]" />
                                </div>
                                <span className="text-sm font-black text-slate-300 uppercase tracking-[0.3em] ml-2">Analizuojami įkalčiai...</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MysteryMathDetectiveGame;
